import React, { useContext, useEffect, useState } from "react";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";
import { toast } from "react-toastify";
import moment from "moment";
import jsPDF from "jspdf";
import "jspdf-autotable";

export default function VostroExpenseReport() {
  const { state } = useContext(NoteState);
  const { branch_id } = state;

  const F11_BRANCH_ID = 15;
  const G13_BRANCH_ID = 1;
  const [filters, setFilters] = useState({
    date: new Date().toLocaleDateString("en-CA"),
  });
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    if (!filters.date) {
      toast.error("Please select a date", { autoClose: 3000 });
      return;
    }

    setLoading(true);
    try {
      const response = await UserServices.getVostroExpenseReport(
        null,
        filters.date
      );
      setRecords(response.data.data || []);
    } catch (err) {
      console.error(err);
      setRecords([]);
      toast.error("No record found", { autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const updateItemField = (recordIndex, itemIndex, field, value) => {
    const updated = [...records];
    updated[recordIndex].items[itemIndex][field] = value;
    setRecords(updated);
  };

  const saveChanges = async () => {
    if (!records.length) {
      return;
    }

    const items = records.flatMap((record) =>
      record.items.map((item) => ({
        id: item.id,
        paid_amount: Number(item.paid_amount) || 0,
        approved_amount: Number(item.approved_amount) || 0,
        is_paid: item.is_paid ? 1 : 0,
        is_approved: item.is_approved ? 1 : 0,
      }))
    );

    setLoading(true);
    try {
      const response = await UserServices.bulkUpdateVostroExpenseItems(items);
      if (response.status === 204) {
        toast.success("Expenses updated", { autoClose: 3000 });
        fetchReport();
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to update expenses", { autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (value) => {
    const num = Number(value || 0);
    return Number.isNaN(num) ? "0" : num.toLocaleString("en-US");
  };

  const buildItems = (record) => (record && record.items ? record.items : []);

  const aggregateByBranch = (data) => {
    const grouped = new Map();

    data.forEach((record) => {
      const key = record.branch_id;
      if (!grouped.has(key)) {
        grouped.set(key, {
          branch_id: record.branch_id,
          branch_name: record.branch_name,
          date: record.date,
          bank_funds: Number(record.bank_funds || 0),
          charity_cash: Number(record.charity_cash || 0),
          gst_cash: Number(record.gst_cash || 0),
          cash_in_hand: Number(record.cash_in_hand || 0),
          notes: record.notes ? [record.notes] : [],
          items: [...(record.items || [])],
        });
      } else {
        const existing = grouped.get(key);
        existing.bank_funds += Number(record.bank_funds || 0);
        existing.charity_cash += Number(record.charity_cash || 0);
        existing.gst_cash += Number(record.gst_cash || 0);
        existing.cash_in_hand += Number(record.cash_in_hand || 0);
        if (record.notes) {
          existing.notes.push(record.notes);
        }
        existing.items.push(...(record.items || []));
      }
    });

    return Array.from(grouped.values()).map((record) => {
      const uniqueNotes = Array.from(new Set(record.notes));
      return {
        ...record,
        notes: uniqueNotes.join(" | "),
      };
    });
  };

  const generatePdf = () => {
    const doc = new jsPDF("p", "mm", "letter");
    const aggregated = aggregateByBranch(records);
    const leftRecord = aggregated.find((r) => r.branch_id === F11_BRANCH_ID) || null;
    const rightRecord = aggregated.find((r) => r.branch_id === G13_BRANCH_ID) || null;
    const leftItems = buildItems(leftRecord);
    const rightItems = buildItems(rightRecord);

    const maxRows = Math.max(leftItems.length, rightItems.length);
    const titleText = `Expenses ${moment(filters.date).format("DD-MMM-YYYY")}`;

    const pageWidth = doc.internal.pageSize.getWidth();
    const headerX = 10;
    const headerWidth = pageWidth - 20;
    const headerY = 10;
    const headerHeight = 8;

    doc.setFillColor(232, 0, 43);
    doc.rect(headerX, headerY, headerWidth, headerHeight, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.text(titleText, pageWidth / 2, headerY + 5.5, { align: "center" });

    const rows = [];
    const greenStyle = { fillColor: [0, 153, 51], textColor: 255, halign: "center", fontStyle: "bold" };
    const headerStyle = { fillColor: [217, 217, 217], fontStyle: "bold", halign: "center" };

    rows.push([
      { content: "F-11", colSpan: 2, styles: greenStyle },
      { content: "G-13", colSpan: 2, styles: greenStyle },
    ]);
    rows.push([
      "F-11 Bank Funds",
      formatAmount(leftRecord?.bank_funds),
      "G-13 Bank Funds",
      formatAmount(rightRecord?.bank_funds),
    ]);
    rows.push([
      "Charity Cash",
      formatAmount(leftRecord?.charity_cash),
      "Charity Cash",
      formatAmount(rightRecord?.charity_cash),
    ]);
    rows.push([
      "GST Cash",
      formatAmount(leftRecord?.gst_cash),
      "GST Cash",
      formatAmount(rightRecord?.gst_cash),
    ]);
    rows.push([
      "F-11 Cash in Hand",
      formatAmount(leftRecord?.cash_in_hand),
      "G-13 Cash in Hand",
      formatAmount(rightRecord?.cash_in_hand),
    ]);
    rows.push([
      "Notes",
      leftRecord?.notes || "",
      "Notes",
      rightRecord?.notes || "",
    ]);
    rows.push([
      { content: "Expense", styles: headerStyle },
      { content: "Amount", styles: headerStyle },
      { content: "Expense", styles: headerStyle },
      { content: "Amount", styles: headerStyle },
    ]);

    for (let i = 0; i < maxRows; i += 1) {
      rows.push([
        leftItems[i]?.description || "",
        formatAmount(leftItems[i]?.amount),
        rightItems[i]?.description || "",
        formatAmount(rightItems[i]?.amount),
      ]);
    }

    const leftTotal = leftItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const rightTotal = rightItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    rows.push([
      { content: "Total", styles: headerStyle },
      { content: formatAmount(leftTotal), styles: headerStyle },
      { content: "Total", styles: headerStyle },
      { content: formatAmount(rightTotal), styles: headerStyle },
    ]);

    doc.setTextColor(0, 0, 0);
    doc.autoTable({
      body: rows,
      startY: 22,
      theme: "grid",
      styles: {
        fontSize: 8,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      margin: { left: 10, right: 10 },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { cellWidth: 28, halign: "right" },
        2: { cellWidth: 70 },
        3: { cellWidth: 28, halign: "right" },
      },
    });

    doc.save(`Daily_Expense_${filters.date}.pdf`);
  };

  const exportLink = `${process.env.REACT_APP_API_Link}/finance/vostro-expense/export?date=${filters.date}`;

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />
          <div className="card">
            <div className="card-header py-3">
              <div className="row">
                <div className="col-md-6">
                  <h5 className="mb-0 font-20">Daily Expense Report</h5>
                </div>
                <div className="col-md-6 text-end">
                  <button
                    className="btn btn-primary mx-2"
                    onClick={generatePdf}
                    disabled={!records.length}
                  >
                    <i className="bi bi-file-earmark-pdf"></i> PDF
                  </button>
                  <a
                    className="btn btn-success"
                    href={exportLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <i className="bi bi-file-earmark-excel"></i> Excel
                  </a>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3">
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="date"
                    value={filters.date}
                    onChange={handleFilterChange}
                  />
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <button
                    className="btn btn-primary w-100"
                    onClick={fetchReport}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Get Report"}
                  </button>
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <button
                    className="btn btn-success w-100"
                    onClick={saveChanges}
                    disabled={loading || !records.length}
                  >
                    Save Changes
                  </button>
                </div>
              </div>

              {records.length === 0 ? (
                <div className="text-center mt-4">No record found</div>
              ) : (
                <>
                  {(() => {
                    const aggregated = aggregateByBranch(records);
                    const leftRecord = aggregated.find((r) => r.branch_id === F11_BRANCH_ID) || null;
                    const rightRecord = aggregated.find((r) => r.branch_id === G13_BRANCH_ID) || null;
                    const leftItems = buildItems(leftRecord);
                    const rightItems = buildItems(rightRecord);
                    const maxRows = Math.max(leftItems.length, rightItems.length);
                    const leftTotal = leftItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);
                    const rightTotal = rightItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);

                    return (
                      <div className="vostro-report-wrapper mt-4">
                        <table className="vostro-report-table w-100">
                          <thead>
                            <tr>
                              <th className="vostro-report-title" colSpan="4">
                                Expenses {moment(filters.date).format("DD-MMM-YYYY")}
                              </th>
                            </tr>
                            <tr>
                              <th className="vostro-report-branch" colSpan="2">F-11</th>
                              <th className="vostro-report-branch" colSpan="2">G-13</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="vostro-report-label">F-11 Bank Funds</td>
                              <td className="vostro-report-value">{formatAmount(leftRecord?.bank_funds)}</td>
                              <td className="vostro-report-label">G-13 Bank Funds</td>
                              <td className="vostro-report-value">{formatAmount(rightRecord?.bank_funds)}</td>
                            </tr>
                            <tr>
                              <td className="vostro-report-label">Charity Cash</td>
                              <td className="vostro-report-value">{formatAmount(leftRecord?.charity_cash)}</td>
                              <td className="vostro-report-label">Charity Cash</td>
                              <td className="vostro-report-value">{formatAmount(rightRecord?.charity_cash)}</td>
                            </tr>
                            <tr>
                              <td className="vostro-report-label">GST Cash</td>
                              <td className="vostro-report-value">{formatAmount(leftRecord?.gst_cash)}</td>
                              <td className="vostro-report-label">GST Cash</td>
                              <td className="vostro-report-value">{formatAmount(rightRecord?.gst_cash)}</td>
                            </tr>
                            <tr>
                              <td className="vostro-report-label">F-11 Cash in Hand</td>
                              <td className="vostro-report-value">{formatAmount(leftRecord?.cash_in_hand)}</td>
                              <td className="vostro-report-label">G-13 Cash in Hand</td>
                              <td className="vostro-report-value">{formatAmount(rightRecord?.cash_in_hand)}</td>
                            </tr>
                            <tr>
                              <td className="vostro-report-label">Notes</td>
                              <td className="vostro-report-notes">{leftRecord?.notes || ""}</td>
                              <td className="vostro-report-label">Notes</td>
                              <td className="vostro-report-notes">{rightRecord?.notes || ""}</td>
                            </tr>
                            <tr className="vostro-report-subhead">
                              <td>Expense</td>
                              <td>Amount</td>
                              <td>Expense</td>
                              <td>Amount</td>
                            </tr>
                            {Array.from({ length: maxRows }).map((_, index) => (
                              <tr key={index}>
                                <td>{leftItems[index]?.description || ""}</td>
                                <td className="vostro-report-value">{formatAmount(leftItems[index]?.amount)}</td>
                                <td>{rightItems[index]?.description || ""}</td>
                                <td className="vostro-report-value">{formatAmount(rightItems[index]?.amount)}</td>
                              </tr>
                            ))}
                            <tr className="vostro-report-total">
                              <td>Total</td>
                              <td className="vostro-report-value">{formatAmount(leftTotal)}</td>
                              <td>Total</td>
                              <td className="vostro-report-value">{formatAmount(rightTotal)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}

                  <div className="mt-4">
                    <h6 className="text-uppercase">Manage Payments / Approvals</h6>
                    <div className="table-responsive mt-2">
                      <table className="table table-striped table-borderless table-style-1">
                        <thead>
                          <tr>
                            <th>Branch</th>
                            <th>Description</th>
                            <th>Amount</th>
                            <th>Paid Amount</th>
                            <th>Paid</th>
                            <th>Approved Amount</th>
                            <th>Approved</th>
                          </tr>
                        </thead>
                        <tbody>
                          {records.map((record, recordIndex) =>
                            record.items.map((item, itemIndex) => (
                              <tr key={item.id}>
                                <td>{record.branch_name || record.branch_id}</td>
                                <td>{item.description}</td>
                                <td>{formatAmount(item.amount)}</td>
                                <td>
                                  <input
                                    type="number"
                                    className="form-control"
                                    value={item.paid_amount}
                                    onChange={(e) =>
                                      updateItemField(
                                        recordIndex,
                                        itemIndex,
                                        "paid_amount",
                                        e.target.value
                                      )
                                    }
                                  />
                                </td>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={item.is_paid === 1 || item.is_paid === true}
                                    onChange={(e) =>
                                      updateItemField(
                                        recordIndex,
                                        itemIndex,
                                        "is_paid",
                                        e.target.checked ? 1 : 0
                                      )
                                    }
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    className="form-control"
                                    value={item.approved_amount}
                                    onChange={(e) =>
                                      updateItemField(
                                        recordIndex,
                                        itemIndex,
                                        "approved_amount",
                                        e.target.value
                                      )
                                    }
                                  />
                                </td>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={item.is_approved === 1 || item.is_approved === true}
                                    onChange={(e) =>
                                      updateItemField(
                                        recordIndex,
                                        itemIndex,
                                        "is_approved",
                                        e.target.checked ? 1 : 0
                                      )
                                    }
                                  />
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
